import { useEffect, useState } from "react";
import { getGameLogoUrl, slugToColor, getGameInitials } from "@/lib/game-logos";

interface GameLogoProps {
  gameSlug: string;
  gameName: string;
  logoUrl?: string | null;
  size?: "sm" | "md" | "lg" | "xl" | "hero";
  className?: string;
  priority?: boolean;
}

const sizeMap = {
  sm: "h-16 w-16 text-sm",
  md: "h-20 w-20 text-base",
  lg: "h-28 w-28 text-lg",
  xl: "h-32 w-32 text-xl",
  hero: "h-48 w-48 md:h-56 md:w-56 text-3xl",
};

const sizePx: Record<string, number> = {
  sm: 64,
  md: 80,
  lg: 112,
  xl: 128,
  hero: 224,
};

export function GameLogo({ gameSlug, gameName, logoUrl: dbLogoUrl, size = "md", className = "", priority = false }: GameLogoProps) {
  const logoUrl = dbLogoUrl || getGameLogoUrl(gameSlug, gameName);
  const sizeClass = sizeMap[size];
  const px = sizePx[size];
  const [imgError, setImgError] = useState(false);

  // Use a stable cache-buster (per-day) so images cache effectively
  const cacheBuster = logoUrl && logoUrl.includes("/storage/v1/object/public/")
    ? Math.floor(Date.now() / 86400000)
    : null;
  const finalUrl = cacheBuster ? `${logoUrl}?v=${cacheBuster}` : logoUrl;

  useEffect(() => {
    setImgError(false);
  }, [finalUrl]);

  if (finalUrl && !imgError) {
    return (
      <img
        src={finalUrl}
        alt={`${gameName} logo`}
        width={px}
        height={px}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        className={`${sizeClass} object-contain rounded ${className}`}
        onError={() => setImgError(true)}
        {...(priority ? { fetchPriority: "high" as const } : {})}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} rounded-full flex items-center justify-center font-bold text-white shrink-0 ${className}`}
      style={{ backgroundColor: slugToColor(gameSlug) }}
      aria-label={`${gameName} logo`}
    >
      {getGameInitials(gameName)}
    </div>
  );
}
